import type { ReactElement } from 'react';
import {
  PeopleIcon,
  KeyIcon,
  SupplierIcon,
  OfficeIcon,
  HistoryIcon,
  SettingsIcon,
  ViewListIcon,
  ViewColumnIcon,
  AssignmentIcon,
  PaletteIcon,
  SyncIcon,
  ListAltIcon,
  HelpIcon,
  FilterListIcon,
  AdminPanelSettingsIcon,
  Inventory2Icon,
  MenuBookIcon,
  VisibilityIcon,
  CloudUploadIcon,
  GitHubIcon,
} from '@atlas/ui';

/**
 * Category buckets that group the admin tabs. Ordering here is the display
 * order in both the grouped tab strip (AdminLayout) and the `/admin` landing
 * grid (AdminLandingPage) — this array is the single source of truth for the
 * taxonomy, so neither consumer redefines it.
 */
export type AdminCategoryId =
  | 'peopleAccess'
  | 'productDataModel'
  | 'evaluationsPricing'
  | 'content'
  | 'organization'
  | 'integrationsSystem';

export interface AdminCategory {
  id: AdminCategoryId;
  labelKey: string;
  descriptionKey: string;
  icon: ReactElement;
}

export const ADMIN_CATEGORIES: AdminCategory[] = [
  {
    id: 'peopleAccess',
    labelKey: 'admin.categories.peopleAccess.label',
    descriptionKey: 'admin.categories.peopleAccess.description',
    icon: <AdminPanelSettingsIcon fontSize="small" />,
  },
  {
    id: 'productDataModel',
    labelKey: 'admin.categories.productDataModel.label',
    descriptionKey: 'admin.categories.productDataModel.description',
    icon: <Inventory2Icon fontSize="small" />,
  },
  {
    id: 'evaluationsPricing',
    labelKey: 'admin.categories.evaluationsPricing.label',
    descriptionKey: 'admin.categories.evaluationsPricing.description',
    icon: <AssignmentIcon fontSize="small" />,
  },
  {
    id: 'content',
    labelKey: 'admin.categories.content.label',
    descriptionKey: 'admin.categories.content.description',
    icon: <MenuBookIcon fontSize="small" />,
  },
  // Reference data an admin edits as part of running the business — kept apart
  // from `integrationsSystem` because suppliers and offices are records, not
  // wiring. They shared that bucket until 2026-08-14 and were routinely hunted
  // for under "Integrations".
  {
    id: 'organization',
    labelKey: 'admin.categories.organization.label',
    descriptionKey: 'admin.categories.organization.description',
    icon: <OfficeIcon fontSize="small" />,
  },
  {
    id: 'integrationsSystem',
    labelKey: 'admin.categories.integrationsSystem.label',
    descriptionKey: 'admin.categories.integrationsSystem.description',
    icon: <SettingsIcon fontSize="small" />,
  },
];

export interface AdminTab {
  value: string;
  labelKey: string;
  icon: ReactElement;
  path: string;
  matchPattern: string;
  /**
   * A role list here is a HARD lock that no setting can widen — reserved for
   * the tabs where granting access would hand over the keys: accounts, API
   * keys, Entra group mapping, this page's own controls, login history,
   * backups, System Settings, and the credential-bearing integrations.
   *
   * Every other tab omits it, which means "whoever Feature Visibility says".
   * Their previous admin/PM lists were seeded into the settings document by
   * migration 20260814-04, so an admin can hand one out without a deploy.
   */
  roles?: ('admin' | 'pm')[];
  category: AdminCategoryId;
}

export const ADMIN_TABS: AdminTab[] = [
  { value: 'users', labelKey: 'admin.tabs.users', icon: <PeopleIcon fontSize="small" />, path: '/admin/users', matchPattern: '/admin/users/*', roles: ['admin'], category: 'peopleAccess' },
  { value: 'api-keys', labelKey: 'admin.tabs.apiKeys', icon: <KeyIcon fontSize="small" />, path: '/admin/api-keys', matchPattern: '/admin/api-keys/*', roles: ['admin'], category: 'peopleAccess' },
  // Entra group -> ATLAS role mapping. Sits with users because it is a
  // permission control, not an integration setting: what it edits is who has
  // which role, the integration merely supplies the membership.
  { value: 'entra-groups', labelKey: 'admin.tabs.entraGroups', icon: <PeopleIcon fontSize="small" />, path: '/admin/entra-groups', matchPattern: '/admin/entra-groups/*', roles: ['admin'], category: 'peopleAccess' },
  // Sits with users/Entra groups for the same reason those do: what it edits is
  // what a role may reach, which is a permission control, not a system setting.
  { value: 'feature-visibility', labelKey: 'admin.tabs.featureVisibility', icon: <VisibilityIcon fontSize="small" />, path: '/admin/feature-visibility', matchPattern: '/admin/feature-visibility/*', roles: ['admin'], category: 'peopleAccess' },
  // Who gets editor access to each brand's Asana Go Live and PDLC projects
  // (#1106). A permission control, so it sits with users and Entra groups.
  // `labelKey` is plain English, not a key: t() returns an unknown key as-is,
  // and a real key obliges regenerating nine locales through the
  // Anthropic-backed generator (same reason as the PDLC Board nav item).
  { value: 'asana-access', labelKey: 'Asana access', icon: <PeopleIcon fontSize="small" />, path: '/admin/asana-access', matchPattern: '/admin/asana-access/*', roles: ['admin'], category: 'peopleAccess' },
  { value: 'login-logs', labelKey: 'admin.tabs.loginLogs', icon: <HistoryIcon fontSize="small" />, path: '/admin/login-logs', matchPattern: '/admin/login-logs/*', roles: ['admin'], category: 'peopleAccess' },
  { value: 'metadata-fields', labelKey: 'admin.tabs.productFields', icon: <ViewColumnIcon fontSize="small" />, path: '/admin/metadata-fields', matchPattern: '/admin/metadata-fields/*', category: 'productDataModel' },
  { value: 'product-types', labelKey: 'admin.tabs.productTypes', icon: <ViewListIcon fontSize="small" />, path: '/admin/product-types', matchPattern: '/admin/product-types/*', category: 'productDataModel' },
  { value: 'barcode-item-prefixes', labelKey: 'admin.tabs.barcodeItemPrefixes', icon: <ViewListIcon fontSize="small" />, path: '/admin/barcode-item-prefixes', matchPattern: '/admin/barcode-item-prefixes/*', category: 'productDataModel' },
  { value: 'shipment-stages', labelKey: 'admin.tabs.shipmentStages', icon: <ViewListIcon fontSize="small" />, path: '/admin/shipment-stages', matchPattern: '/admin/shipment-stages/*', category: 'productDataModel' },
  { value: 'spec-modules', labelKey: 'admin.tabs.specModules', icon: <SettingsIcon fontSize="small" />, path: '/admin/spec-modules', matchPattern: '/admin/spec-modules/*', category: 'productDataModel' },
  { value: 'fixture-aliases', labelKey: 'admin.tabs.fixtureAliases', icon: <FilterListIcon fontSize="small" />, path: '/admin/fixture-aliases', matchPattern: '/admin/fixture-aliases/*', category: 'productDataModel' },
  { value: 'asset-types', labelKey: 'admin.tabs.assetTypes', icon: <KeyIcon fontSize="small" />, path: '/admin/asset-types', matchPattern: '/admin/asset-types/*', category: 'productDataModel' },
  { value: 'asset-lists', labelKey: 'admin.tabs.assetLists', icon: <ListAltIcon fontSize="small" />, path: '/admin/asset-lists', matchPattern: '/admin/asset-lists/*', category: 'productDataModel' },
  { value: 'eval-forms', labelKey: 'admin.tabs.evalForms', icon: <AssignmentIcon fontSize="small" />, path: '/admin/eval-forms', matchPattern: '/admin/eval-forms/*', category: 'evaluationsPricing' },
  // Pricing Config and Price Lists deliberately have NO tab here: both moved to
  // the main sidebar on 2026-08-26 so PMs would find them, exactly as bulletins
  // did. /admin/pricing-config and /admin/price-lists still redirect. Don't
  // re-add them — two entry points is how bulletins got lost.
  // Price List CONFIG is a different thing: which fields each division's list
  // shows and which are editable (#908). Admin-only, like the PUT behind it.
  { value: 'price-list-config', labelKey: 'admin.tabs.priceLists', icon: <ListAltIcon fontSize="small" />, path: '/admin/price-list-config', matchPattern: '/admin/price-list-config/*', roles: ['admin'], category: 'evaluationsPricing' },
  { value: 'help', labelKey: 'admin.tabs.help', icon: <HelpIcon fontSize="small" />, path: '/admin/help', matchPattern: '/admin/help/*', category: 'content' },
  // Bulletins deliberately has NO tab here: composing moved out to
  // /bulletins/manage on 2026-07-23 so PMs would find it. /admin/bulletins still
  // redirects there. Don't re-add it — two entry points is how it got lost.
  // The external-recipient lists themselves — NOT bulletin composing, which
  // stays at /bulletins/manage. Same lists, surfaced where an audience is
  // managed rather than only from inside the composer.
  { value: 'notification-lists', labelKey: 'admin.tabs.notificationLists', icon: <ListAltIcon fontSize="small" />, path: '/admin/notification-lists', matchPattern: '/admin/notification-lists/*', category: 'content' },
  { value: 'phase-colors', labelKey: 'admin.tabs.phaseColors', icon: <PaletteIcon fontSize="small" />, path: '/admin/phase-colors', matchPattern: '/admin/phase-colors/*', category: 'content' },
  { value: 'suppliers', labelKey: 'admin.tabs.suppliers', icon: <SupplierIcon fontSize="small" />, path: '/admin/suppliers', matchPattern: '/admin/suppliers/*', category: 'organization' },
  { value: 'offices', labelKey: 'admin.tabs.offices', icon: <OfficeIcon fontSize="small" />, path: '/admin/offices', matchPattern: '/admin/offices/*', category: 'organization' },
  { value: 'smartsheet', labelKey: 'admin.tabs.smartsheet', icon: <SyncIcon fontSize="small" />, path: '/admin/smartsheet', matchPattern: '/admin/smartsheet/*', roles: ['admin'], category: 'integrationsSystem' },
  { value: 'standardsBot', labelKey: 'admin.tabs.standardsBot', icon: <MenuBookIcon fontSize="small" />, path: '/admin/standards-bot', matchPattern: '/admin/standards-bot/*', roles: ['admin'], category: 'integrationsSystem' },
  // Backups and Dev Dashboard were cards stacked inside the DevOps page until
  // 2026-08-14. Each is a tool you go looking for by name, and neither was
  // findable from the sidebar filter while it lived inside another page.
  { value: 'backups', labelKey: 'admin.tabs.backups', icon: <CloudUploadIcon fontSize="small" />, path: '/admin/backups', matchPattern: '/admin/backups/*', roles: ['admin'], category: 'integrationsSystem' },
  { value: 'dev-dashboard', labelKey: 'admin.tabs.devDashboard', icon: <GitHubIcon fontSize="small" />, path: '/admin/dev-dashboard', matchPattern: '/admin/dev-dashboard/*', category: 'integrationsSystem' },
  // `value` stays 'devops' even though it now reads "System Settings": it is the
  // feature id in NON_HIDEABLE and the hidden-features store, so renaming it
  // would silently un-protect the tab and orphan any stored toggle.
  { value: 'devops', labelKey: 'admin.tabs.devops', icon: <SettingsIcon fontSize="small" />, path: '/admin/devops', matchPattern: '/admin/devops/*', roles: ['admin'], category: 'integrationsSystem' },
];

export interface AdminCategoryGroup {
  category: AdminCategory;
  tabs: AdminTab[];
}

/**
 * Groups the given tabs into their categories, preserving `ADMIN_CATEGORIES`
 * order and dropping any category that ends up with no tabs (e.g. after a
 * role/hidden-feature filter). Shared by the tab strip and the landing grid.
 */
export function groupTabsByCategory(tabs: AdminTab[]): AdminCategoryGroup[] {
  return ADMIN_CATEGORIES.map((category) => ({
    category,
    tabs: tabs.filter((tab) => tab.category === category.id),
  })).filter((group) => group.tabs.length > 0);
}
